// src/admin/AdminDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { signOut } from "firebase/auth";

import { auth, db } from "../services/firebase";

const formatPrice = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatDate = (timestamp) => {
  if (!timestamp) return "—";

  try {
    const date =
      typeof timestamp.toDate === "function"
        ? timestamp.toDate()
        : new Date(timestamp);

    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  } catch {
    return "—";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "paid":
      return "Payée";

    case "confirmed":
      return "Confirmée";

    case "processing":
      return "En traitement";

    case "completed":
      return "Terminée";

    case "cancelled":
      return "Annulée";

    case "failed":
      return "Échec";

    case "pending":
    default:
      return "En attente";
  }
};

const getStatusClass = (status) => {
  switch (status) {
    case "paid":
      return "bg-success";

    case "confirmed":
      return "bg-primary";

    case "processing":
      return "bg-info text-dark";

    case "completed":
      return "bg-dark";

    case "cancelled":
    case "failed":
      return "bg-danger";

    case "pending":
    default:
      return "bg-warning text-dark";
  }
};

function DashboardSkeleton() {
  return (
    <div className="container py-4">
      <div className="placeholder-glow mb-4">
        <span
          className="placeholder col-4"
          style={{ height: 30 }}
        />
      </div>

      <div className="row g-3">
        {[1, 2, 3, 4].map((item) => (
          <div
            className="col-6 col-lg-3"
            key={item}
          >
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body placeholder-glow">
                <span className="placeholder col-6 mb-3" />
                <br />

                <span
                  className="placeholder col-4"
                  style={{ height: 28 }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const [loadingProducts, setLoadingProducts] =
    useState(true);

  const [loadingOrders, setLoadingOrders] =
    useState(true);

  /*
   * =========================================================
   * PRODUITS TEMPS RÉEL
   * =========================================================
   */

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "products_prosoft"),

      (snapshot) => {
        const list = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        setProducts(list);
        setLoadingProducts(false);
      },

      (error) => {
        console.error(
          "Erreur chargement produits Dashboard :",
          error
        );

        setLoadingProducts(false);
      }
    );

    return () => unsubscribe();
  }, []);

  /*
   * =========================================================
   * COMMANDES TEMPS RÉEL
   * =========================================================
   */

  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders_prosoft"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      ordersQuery,

      (snapshot) => {
        const list = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        setOrders(list);
        setLoadingOrders(false);
      },

      (error) => {
        console.error(
          "Erreur chargement commandes Dashboard :",
          error
        );

        setLoadingOrders(false);
      }
    );

    return () => unsubscribe();
  }, []);

  /*
   * =========================================================
   * STATISTIQUES
   * =========================================================
   */

  const stats = useMemo(() => {
    const totalProducts = products.length;

    const availableProducts = products.filter(
      (product) => product.inStock
    ).length;

    const totalStock = products.reduce(
      (sum, product) =>
        sum + (Number(product.stockQty) || 0),
      0
    );

    const totalOrders = orders.length;

    const pendingOrders = orders.filter(
      (order) =>
        (order.status || "pending") === "pending"
    ).length;

    const paidOrders = orders.filter(
      (order) =>
        order.status === "paid" ||
        order.status === "completed"
    ).length;

    const revenue = orders
      .filter(
        (order) =>
          order.status === "paid" ||
          order.status === "completed"
      )
      .reduce(
        (sum, order) =>
          sum + (Number(order.amount) || 0),
        0
      );

    return {
      totalProducts,
      availableProducts,
      totalStock,
      totalOrders,
      pendingOrders,
      paidOrders,
      revenue,
    };
  }, [products, orders]);

  /*
   * =========================================================
   * DÉCONNEXION
   * =========================================================
   */

  const handleLogout = async () => {
    try {
      await signOut(auth);

      navigate("/admin/login", {
        replace: true,
      });
    } catch (error) {
      console.error(
        "Erreur déconnexion :",
        error
      );
    }
  };

  const loading =
    loadingProducts || loadingOrders;

  if (loading) {
    return <DashboardSkeleton />;
  }

  const latestOrders = orders.slice(0, 6);

  return (
    <>
      <style>
        {`
          .admin-dashboard {
            min-height: 100vh;
            background: #f7f8fa;
          }

          .dashboard-stat-card {
            transition:
              transform .2s ease,
              box-shadow .2s ease;
          }

          .dashboard-stat-card:hover {
            transform: translateY(-3px);
            box-shadow:
              0 8px 24px rgba(0,0,0,.08) !important;
          }

          .dashboard-icon {
            width: 46px;
            height: 46px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
          }

          .dashboard-action-card {
            transition:
              transform .2s ease,
              border-color .2s ease;
          }

          .dashboard-action-card:hover {
            transform: translateY(-2px);
          }
        `}
      </style>

      <div className="admin-dashboard">
        <div className="container py-4">

          {/* =====================================================
              HEADER
          ====================================================== */}

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <div className="text-muted small mb-1">
                Administration
              </div>

              <h3 className="fw-bold mb-1">
                Dashboard
              </h3>

              <div className="text-muted small">
                Vue d'ensemble de ProSoft Tools
              </div>
            </div>

            <div className="d-flex gap-2">
              <a
                href="/"
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                Voir le site
              </a>

              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleLogout}
              >
                Déconnexion
              </button>
            </div>
          </div>

          {/* =====================================================
              STATISTIQUES
          ====================================================== */}

          <div className="row g-3 mb-4">

            {/* PRODUITS */}

            <div className="col-6 col-xl-3">
              <div className="card dashboard-stat-card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted">
                        Produits
                      </div>

                      <div className="fs-3 fw-bold mt-1">
                        {stats.totalProducts}
                      </div>

                      <div className="small text-success mt-1">
                        {stats.availableProducts} disponibles
                      </div>
                    </div>

                    <div className="dashboard-icon bg-primary-subtle">
                      📦
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STOCK */}

            <div className="col-6 col-xl-3">
              <div className="card dashboard-stat-card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted">
                        Stock total
                      </div>

                      <div className="fs-3 fw-bold mt-1">
                        {stats.totalStock}
                      </div>

                      <div className="small text-muted mt-1">
                        unités
                      </div>
                    </div>

                    <div className="dashboard-icon bg-info-subtle">
                      🏷️
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* COMMANDES */}

            <div className="col-6 col-xl-3">
              <div className="card dashboard-stat-card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted">
                        Commandes
                      </div>

                      <div className="fs-3 fw-bold mt-1">
                        {stats.totalOrders}
                      </div>

                      <div className="small text-warning mt-1">
                        {stats.pendingOrders} en attente
                      </div>
                    </div>

                    <div className="dashboard-icon bg-warning-subtle">
                      🛒
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CA */}

            <div className="col-6 col-xl-3">
              <div className="card dashboard-stat-card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted">
                        Encaissé
                      </div>

                      <div className="fs-5 fw-bold text-primary mt-2">
                        {formatPrice(stats.revenue)}
                      </div>

                      <div className="small text-muted">
                        CFA
                      </div>
                    </div>

                    <div className="dashboard-icon bg-success-subtle">
                      💰
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* =====================================================
              ACCÈS RAPIDES
          ====================================================== */}

          <div className="mb-4">
            <h6 className="fw-bold mb-3">
              Accès rapides
            </h6>

            <div className="row g-3">

              <div className="col-md-4">
                <Link
                  to="/admin/products"
                  className="text-decoration-none text-dark"
                >
                  <div className="card dashboard-action-card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body">
                      <div
                        className="mb-3"
                        style={{ fontSize: 30 }}
                      >
                        📦
                      </div>

                      <h6 className="fw-bold">
                        Produits
                      </h6>

                      <p className="small text-muted mb-3">
                        Ajouter, modifier et gérer
                        les produits du catalogue.
                      </p>

                      <span className="text-primary small fw-semibold">
                        Gérer les produits →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <Link
                  to="/admin/orders"
                  className="text-decoration-none text-dark"
                >
                  <div className="card dashboard-action-card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body">
                      <div
                        className="mb-3"
                        style={{ fontSize: 30 }}
                      >
                        🛒
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <h6 className="fw-bold mb-0">
                          Commandes
                        </h6>

                        {stats.pendingOrders > 0 && (
                          <span className="badge bg-danger rounded-pill">
                            {stats.pendingOrders}
                          </span>
                        )}
                      </div>

                      <p className="small text-muted mt-2 mb-3">
                        Consulter et traiter les
                        commandes des clients.
                      </p>

                      <span className="text-primary small fw-semibold">
                        Voir les commandes →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <a
                  href="/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none text-dark"
                >
                  <div className="card dashboard-action-card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body">
                      <div
                        className="mb-3"
                        style={{ fontSize: 30 }}
                      >
                        🌐
                      </div>

                      <h6 className="fw-bold">
                        Boutique
                      </h6>

                      <p className="small text-muted mb-3">
                        Ouvrir la boutique visible
                        par les clients.
                      </p>

                      <span className="text-primary small fw-semibold">
                        Voir le site →
                      </span>
                    </div>
                  </div>
                </a>
              </div>

            </div>
          </div>

          {/* =====================================================
              DERNIÈRES COMMANDES
          ====================================================== */}

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="fw-bold mb-0">
                    Dernières commandes
                  </h6>

                  <div className="small text-muted">
                    Mise à jour en temps réel
                  </div>
                </div>

                <Link
                  to="/admin/orders"
                  className="btn btn-outline-primary btn-sm"
                >
                  Voir tout
                </Link>
              </div>

              {latestOrders.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <div
                    style={{
                      fontSize: 34,
                    }}
                  >
                    🛒
                  </div>

                  <div className="small mt-2">
                    Aucune commande reçue.
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle small mb-0">
                    <thead>
                      <tr>
                        <th>Commande</th>
                        <th>Client</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th>Date</th>
                      </tr>
                    </thead>

                    <tbody>
                      {latestOrders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <Link
                              to="/admin/orders"
                              className="fw-semibold text-decoration-none"
                            >
                              {order.identifier ||
                                order.id}
                            </Link>
                          </td>

                          <td>
                            <div className="fw-semibold">
                              {order.customer?.fullName ||
                                "Client"}
                            </div>

                            <div className="text-muted">
                              {order.customer?.phone ||
                                "—"}
                            </div>
                          </td>

                          <td className="fw-bold">
                            {formatPrice(
                              order.amount
                            )}{" "}
                            CFA
                          </td>

                          <td>
                            <span
                              className={`badge ${getStatusClass(
                                order.status
                              )}`}
                            >
                              {getStatusLabel(
                                order.status
                              )}
                            </span>
                          </td>

                          <td className="text-nowrap text-muted">
                            {formatDate(
                              order.createdAt
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
